import { useState } from "react";
import { HandCoins } from "lucide-react";
import { AdminTitle } from "@/admin/AdminLayout";
import { FilterBar, DecisionButtons } from "@/admin/Decision";
import { StatusBadge } from "@/components/Brand";
import { api, useApi } from "@/lib/api";
import { rp, fmtDate } from "@/lib/format";
import { DocImage } from "@/components/DocImage";
import { LOAN_STEPS, loanStage } from "@/components/LoanTimeline";
import { LoanPhotos, LoanPdfButton, StageButton } from "@/admin/LoanParts";
import { SurveyPanel, DisburseMethod } from "@/admin/SurveyPanel";

const openDoc = async (id) => {
  const w = window.open("", "_blank");
  const r = await api.get(`/loans/docs/${id}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  if (w) w.location.href = url;
  else window.location.href = url;
};

export default function AdminLoans() {
  const { data, reload } = useApi("/admin/loans");
  const refresh = () => { reload(); window.dispatchEvent(new Event("cu-admin-changed")); };
  const [filter, setFilter] = useState("pending");
  const list = (data || []).filter((l) => filter === "all" || l.status === filter || (filter === "pending" && l.status === "disetujui"));
  return (
    <div>
      <AdminTitle icon={HandCoins} title="Pinjaman" sub="Tinjau dan setujui pengajuan pinjaman anggota" />
      <FilterBar value={filter} onChange={setFilter} />
      <div className="space-y-3" data-testid="admin-loans-list">
        {list.length === 0 && <p className="text-sm text-slate-500" data-testid="admin-loans-empty">Tidak ada data.</p>}
        {list.map((l) => (
          <div key={l.loan_id} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap"><b>{l.member_name}</b><span className="text-xs text-slate-500">{l.member_no}</span><StatusBadge status={l.status} />
                <span className="text-[11px] font-semibold text-blue-800 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid={`admin-loan-stage-badge-${l.loan_id}`}>Tahap: {LOAN_STEPS.find((s) => s.key === loanStage(l)).label}</span></div>
              <div className="text-sm mt-1"><b>{rp(l.amount)}</b> • {l.tenor_months} bln • {l.rate}%/bln • angsuran {rp(l.monthly_installment)}</div>
              <div className="text-xs text-slate-500">Tujuan: {l.purpose} • Diajukan {fmtDate(l.created_at)}</div>
              {l.income_monthly > 0 && (
                <div className="text-xs text-slate-700 mt-1" data-testid={`admin-loan-income-${l.loan_id}`}>
                  Penghasilan {rp(l.income_monthly)}/bln • {l.income_source}{l.workplace ? ` • ${l.workplace}` : ""} • {l.job_desc} • {l.work_length}
                  {(() => { const p = (l.monthly_installment / l.income_monthly) * 100; return <span data-testid={`admin-loan-dsr-${l.loan_id}`} className={`ml-1 font-semibold ${p > 30 ? "text-amber-700 bg-amber-100 rounded px-1.5 py-0.5" : "text-emerald-700"}`}>(rasio angsuran {p.toFixed(1).replace(".", ",")}%{p > 30 ? " — melebihi 30%" : ""})</span>; })()}
                </div>
              )}
              {l.documents?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {l.documents.filter((d) => !["ttd", "selfie", "rumah", "survei"].includes(d.doc_type)).map((d) => <button key={d.doc_id} onClick={() => openDoc(d.doc_id)} data-testid={`admin-loan-doc-${d.doc_id}`}
                    className="text-[11px] font-semibold border border-blue-200 text-blue-700 bg-blue-50 rounded-lg px-2 py-1">{d.label}</button>)}
                </div>
              )}
              <LoanPhotos loan={l} />
              {l.documents?.find((d) => d.doc_type === "ttd") && (
                <div className="mt-2" data-testid={`admin-loan-signature-${l.loan_id}`}>
                  <div className="text-[11px] text-slate-500">Tanda tangan anggota{l.agreed_at ? ` • ${fmtDate(l.agreed_at)}` : ""}</div>
                  <DocImage docId={l.documents.find((d) => d.doc_type === "ttd").doc_id} alt="Tanda tangan anggota" className="h-16 w-44 object-contain border border-slate-200 rounded-lg bg-white" />
                </div>
              )}
              {l.status === "aktif" && <div className="text-xs text-slate-700 mt-1">Terbayar {rp(l.paid_amount)} dari {rp(l.total_payable)} • Sisa {rp(l.remaining)}</div>}
              <DisburseMethod loan={l} onDone={refresh} />
              {l.disbursement && <div className="text-xs text-emerald-700 mt-1" data-testid={`admin-loan-disbursed-${l.loan_id}`}>Dicairkan {fmtDate(l.disbursement.at)} • No. {l.disbursement.receipt_no} • oleh {l.disbursement.by_name}</div>}
              <SurveyPanel loan={l} onDone={refresh} />
            </div>
            <div className="flex flex-col items-start lg:items-end gap-2 shrink-0">
              <div className="flex flex-wrap gap-2">
                {l.purpose_category && <LoanPdfButton loan={l} />}
                <StageButton loan={l} onDone={refresh} />
              </div>
              {l.status === "pending" && (!l.stage || (l.stage === "survei" && l.survey?.report)) && <DecisionButtons url={`/admin/loans/${l.loan_id}/decision`} id={l.loan_id} onDone={refresh} />}
              {l.status === "pending" && l.stage === "survei" && !l.survey?.report && <span className="text-[11px] text-amber-700" data-testid={`admin-loan-need-report-${l.loan_id}`}>Isi laporan survei untuk membuka keputusan</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
